import type { GenerationOptions, TalkBrief } from "@hali-shares/deck-schema";
import { useEffect, useRef, useState } from "react";
import { type Asset, type Issue, api } from "../../../lib/api";
import { ConfirmButton, Issues, NumberField, Select, TextArea, TextField } from "../fields";
import { minutesTotal } from "../model";
import type { StudioCtx } from "./ctx";

type CodeChoice = "auto" | "on" | "off";
type Details = { title: string; subtitle: string; presenter: string; event: string; date: string };

const SAVE_MS = 600;

/**
 * Step 1: what the talk is, who it's for and what they should leave with. Talk details save when you leave the card
 * (a version, as they're on the title slide); the brief and settings save as you type (no version).
 */
export function BasicsStep({ ctx }: { ctx: StudioCtx }) {
  const { deck } = ctx;
  const fromDeck = (): Details => ({
    title: deck.title,
    subtitle: deck.subtitle ?? "",
    presenter: deck.presenter,
    event: deck.event ?? "",
    date: deck.date ?? "",
  });
  const [details, setDetails] = useState<Details>(fromDeck);
  const [issues, setIssues] = useState<Issue[]>([]);
  const saveDetails = async () => {
    const was = fromDeck();
    if (JSON.stringify(was) === JSON.stringify(details)) return;
    const found = await ctx.commit(
      {
        ...deck,
        title: details.title.trim(),
        presenter: details.presenter.trim(),
        subtitle: details.subtitle.trim() || undefined,
        event: details.event.trim() || undefined,
        date: details.date || undefined,
      },
      "Talk details",
    );
    setIssues(found);
  };

  // Brief and generation settings: saved a moment after the last change.
  const [brief, setBrief] = useState<TalkBrief>(ctx.settings.brief);
  const [options, setOptions] = useState<GenerationOptions>(ctx.settings.generation);
  const pending = useRef<(() => void) | null>(null);
  const first = useRef(true);
  const { saveSettings } = ctx;
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const save = () => {
      pending.current = null;
      saveSettings({ brief: cleanBrief(brief), generation: clean(options) });
    };
    pending.current = save;
    const t = window.setTimeout(save, SAVE_MS);
    return () => window.clearTimeout(t);
  }, [brief, options, saveSettings]);
  // Leaving the step (or the page) within the moment still saves.
  useEffect(() => () => pending.current?.(), []);

  const codeChoice: CodeChoice = options.code === undefined ? "auto" : options.code ? "on" : "off";
  const set = (patch: Partial<Details>) => setDetails((d) => ({ ...d, ...patch }));

  return (
    <div className="step-page">
      <header className="step-head">
        <p className="step-eyebrow">Step 1 of 4</p>
        <h1 className="step-h">What’s the talk?</h1>
        <p className="step-lead">
          A few facts about the talk and the room. The AI uses them for every suggestion from here on, so the clearer
          the goal, the better the outline and slides.
        </p>
      </header>

      <section
        className="step-card"
        aria-labelledby="basics-talk"
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) void saveDetails();
        }}
      >
        <h2 id="basics-talk" className="step-card-h">
          The talk
        </h2>
        <Issues issues={issues} />
        <TextField label="Title" max={80} value={details.title} onChange={(title) => set({ title })} />
        <TextField
          label="Subtitle"
          hint="Optional. One line under the title."
          max={160}
          value={details.subtitle}
          onChange={(subtitle) => set({ subtitle })}
        />
        <div className="grid-3">
          <TextField
            label="Presenter"
            max={60}
            value={details.presenter}
            onChange={(presenter) => set({ presenter })}
          />
          <TextField label="Event" max={60} value={details.event} onChange={(event) => set({ event })} />
          <div className="field">
            <label className="field-label" htmlFor="talk-date">
              Date
            </label>
            <input
              id="talk-date"
              className="input input-mono"
              type="date"
              value={details.date}
              onChange={(e) => set({ date: e.target.value })}
            />
          </div>
        </div>
      </section>

      <section className="step-card" aria-labelledby="basics-room">
        <h2 id="basics-room" className="step-card-h">
          The room and the goal
        </h2>
        <TextArea
          label="What should people leave with?"
          hint="One or two sentences: what they’ll understand, be able to do, or decide. E.g. “Wire one tool into an agent and know when not to.”"
          max={400}
          value={brief.goal}
          onChange={(goal) => setBrief((b) => ({ ...b, goal: goal || undefined }))}
        />
        <TextArea
          label="Who’s in the room?"
          hint="What they do and what they already know. E.g. “Backend developers, most new to LLMs.”"
          max={400}
          rows={2}
          value={brief.audienceNote}
          onChange={(audienceNote) => setBrief((b) => ({ ...b, audienceNote: audienceNote || undefined }))}
        />
        <div className="grid-3">
          <NumberField
            label="Length (minutes)"
            min={5}
            max={240}
            optional
            value={brief.minutes ?? (minutesTotal(deck) || undefined)}
            onChange={(minutes) => setBrief((b) => ({ ...b, minutes: minutes || undefined }))}
          />
          <Select
            label="Level"
            value={options.audience ?? ""}
            options={[
              { value: "", label: "Any" },
              { value: "beginner", label: "Beginners" },
              { value: "mixed", label: "Mixed" },
              { value: "expert", label: "Experts" },
            ]}
            onChange={(v) => setOptions((o) => ({ ...o, audience: v || undefined }))}
          />
          <Select
            label="Tone"
            value={options.tone ?? ""}
            options={[
              { value: "", label: "Any" },
              { value: "conversational", label: "Conversational" },
              { value: "neutral", label: "Neutral" },
              { value: "formal", label: "Formal" },
            ]}
            onChange={(v) => setOptions((o) => ({ ...o, tone: v || undefined }))}
          />
          <Select
            label="Polls and quizzes"
            value={options.interaction ?? "balanced"}
            options={[
              { value: "light", label: "A few" },
              { value: "balanced", label: "Some" },
              { value: "heavy", label: "Many" },
            ]}
            onChange={(v) => setOptions((o) => ({ ...o, interaction: v }))}
          />
          <Select<CodeChoice>
            label="Code examples"
            value={codeChoice}
            options={[
              { value: "auto", label: "If it fits" },
              { value: "on", label: "Yes" },
              { value: "off", label: "None" },
            ]}
            onChange={(v) => setOptions((o) => ({ ...o, code: v === "auto" ? undefined : v === "on" }))}
          />
        </div>
      </section>

      <section className="step-card" aria-labelledby="basics-material">
        <h2 id="basics-material" className="step-card-h">
          What you already have <span className="step-optional">optional</span>
        </h2>
        <p className="step-card-lead">
          Paste notes, an outline, numbers, links. The AI draws on them for the outline and every slide, and anything
          that doesn’t fit on a slide goes into the speaker notes. Links become references.
        </p>
        <label htmlFor="source" className="field-label">
          Notes
        </label>
        <textarea
          id="source"
          className="source source-short"
          value={ctx.source}
          spellCheck={false}
          placeholder={
            "Rough notes, in any order.\n\n- why this matters to us\n- the numbers from last quarter\n- https://arxiv.org/abs/…"
          }
          onChange={(e) => ctx.setSource(e.target.value)}
        />
        <details className="hints">
          <summary>Shortcuts the AI understands</summary>
          <ul>
            <li>
              <code>## Heading</code> starts a section of the talk.
            </li>
            <li>
              <code>IMAGE: name.png</code> with an <code>ask:</code> line becomes a screenshot to discuss (upload the
              image below).
            </li>
            <li>
              <code>POLL: question?</code> with options on the next line (<code>a / b / c</code>) becomes a live poll.
            </li>
            <li>
              <code>QUIZ: question?</code> with options; mark the right one with <code>*</code>.
            </li>
          </ul>
        </details>
        <Images ctx={ctx} />
      </section>

      <footer className="step-foot">
        <span className="spacer" />
        <button type="button" className="s-btn s-btn-ink s-btn-tall" onClick={() => ctx.goto("outline")}>
          Next: plan the outline
        </button>
      </footer>
    </div>
  );
}

/** The brief as stored: no empty fields, and a length only when it's one the server takes (5 to 240 minutes). */
function cleanBrief(b: TalkBrief): TalkBrief {
  const m = b.minutes === undefined ? undefined : Math.round(b.minutes);
  return clean({ ...b, minutes: m !== undefined && m >= 5 && m <= 240 ? m : undefined });
}

/** Drops empty strings and undefined so the stored settings stay small. */
function clean<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;
}

/** A clip shows its first frame; images show themselves. */
function AssetThumb({ asset }: { asset: Asset }) {
  const src = `/api/assets/${asset.id}`;
  return asset.contentType.startsWith("video/") ? (
    <video src={`${src}#t=0.1`} muted preload="metadata" aria-hidden="true" />
  ) : (
    <img src={src} alt="" />
  );
}

/** This talk's images, older uploads that belong to no talk yet, and the upload button. */
export function Images({ ctx }: { ctx: StudioCtx }) {
  const input = useRef<HTMLInputElement>(null);
  const own = ctx.assets.filter((a) => a.deckId !== null);
  const older = ctx.assets.filter((a) => a.deckId === null);
  return (
    <div className="images">
      <div className="row-gap">
        <button type="button" className="s-btn" onClick={() => input.current?.click()}>
          Upload images or clips
        </button>
        <span className="col-note">Screenshots and diagrams (PNG, JPG, WebP) and clips (MP4, WebM, GIF).</span>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm"
          multiple
          hidden
          onChange={async (e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = "";
            for (const f of files) await ctx.upload(f);
          }}
        />
      </div>
      {own.length > 0 && (
        <div className="assets" aria-label="Uploaded images">
          {own.map((a) => (
            <span key={a.id} className="asset-chip" title={a.filename}>
              <AssetThumb asset={a} />
              {a.filename}
              <ConfirmButton
                className="icon-btn"
                label="×"
                confirmLabel="Delete?"
                onConfirm={() => api.deleteAsset(a.id).then(() => ctx.setAssets((l) => l.filter((x) => x.id !== a.id)))}
              />
            </span>
          ))}
        </div>
      )}
      {older.length > 0 && (
        <div className="assets-older">
          <span className="col-label">Older uploads, in no talk yet</span>
          <div className="assets" aria-label="Older uploads">
            {older.map((a) => (
              <span key={a.id} className="asset-chip" title={a.filename}>
                <AssetThumb asset={a} />
                {a.filename}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() =>
                    api.assignAsset(a.id, ctx.deckId).then(
                      (claimed) => ctx.setAssets((l) => l.map((x) => (x.id === a.id ? claimed : x))),
                      (e: unknown) => ctx.setError(e instanceof Error ? e.message : String(e)),
                    )
                  }
                >
                  Add to this talk
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
